import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Typography,
} from '@shared/ui';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { AVAILABILITY_WEEKDAYS, WEEKDAY_LABELS } from './availabilityTransforms';
import type { AvailabilityWeekday } from './availabilityTypes';

interface CopyDayPopoverProps {
  sourceWeekday: AvailabilityWeekday | null;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onCopy: (sourceWeekday: AvailabilityWeekday, targetWeekdays: AvailabilityWeekday[]) => void;
}

const CopyDayPopover: React.FC<CopyDayPopoverProps> = ({
  sourceWeekday,
  anchorEl,
  onClose,
  onCopy,
}) => {
  const [selectedWeekdays, setSelectedWeekdays] = useState<AvailabilityWeekday[]>([]);
  const open = Boolean(anchorEl) && sourceWeekday !== null;
  const targetWeekdays = useMemo(
    () => AVAILABILITY_WEEKDAYS.filter((weekday) => weekday !== sourceWeekday),
    [sourceWeekday]
  );

  useEffect(() => {
    if (open) {
      setSelectedWeekdays(defaultCopyTargets(sourceWeekday));
    }
  }, [open, sourceWeekday]);

  const selectedSet = new Set(selectedWeekdays);
  const toggleWeekday = (weekday: AvailabilityWeekday) => {
    setSelectedWeekdays((current) =>
      current.includes(weekday)
        ? current.filter((selected) => selected !== weekday)
        : [...current, weekday]
    );
  };

  const handleCopy = () => {
    if (sourceWeekday === null || selectedWeekdays.length === 0) {
      return;
    }
    onCopy(sourceWeekday, selectedWeekdays);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ minWidth: 220, p: 2 }}>
        <Typography sx={{ color: '#A4A4A4', fontSize: 12, fontWeight: 700, mb: 1 }}>
          COPY TO
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={() => setSelectedWeekdays(targetWeekdays)}
          >
            ALL
          </Button>
          <Button type="button" size="small" variant="text" onClick={() => setSelectedWeekdays([])}>
            NONE
          </Button>
        </Box>
        <FormGroup sx={{ mb: 1.5 }}>
          {targetWeekdays.map((weekday) => (
            <FormControlLabel
              key={weekday}
              control={
                <Checkbox
                  checked={selectedSet.has(weekday)}
                  onChange={() => toggleWeekday(weekday)}
                />
              }
              label={WEEKDAY_LABELS[weekday]}
            />
          ))}
        </FormGroup>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button type="button" variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={selectedWeekdays.length === 0}
            onClick={handleCopy}
          >
            Copy
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

function defaultCopyTargets(sourceWeekday: AvailabilityWeekday | null): AvailabilityWeekday[] {
  return AVAILABILITY_WEEKDAYS.filter((weekday) => weekday !== sourceWeekday && weekday <= 5);
}

export default CopyDayPopover;
